
import PageContainer from "../components/CommonComponents/PageContainer/PageContainer";
import MatchPlayerContainer from "../components/MatchComponents/MatchPlayerContainer/MatchPlayerContainer";
import React, {useEffect, useRef, useState} from "react";
import {getMatchClips, getEventById} from '../queries/matchPageQueries'
import {SelectedClipProvider} from "../contexts/selectedClip";
import {LoadError} from "../types/generic";
import ErrorSnackBar from "../components/CommonComponents/ErrorSnackBar/ErrorSnackBar";
import { useSearchParams } from 'react-router-dom';
import { Event } from "../types/eventTypes"
import {
    Clip,
    GroupedClips,
    Highlight,
    KeyMomentClip,
    VideoContentTypes,
    VideoUpdatableFields
} from "../types/clipTypes";
import {useSelectedEvent} from "../contexts/event";
import {deleteClip, updateClipTags} from "../queries/clipQueries";
import {deleteHighlight} from "../queries/highlightQueries";
import {deleteJobClips} from "../queries/jobQueries";
import {useVideoContentTagsOptions} from "../contexts/VideoContentTagsOptions";
import axios from "axios";
import isEqual from 'lodash/isEqual';
import {sortClipByEventStream} from "../utils/utilsClip";
import {useSSEProvider} from "../contexts/SSEProvider";
import {jobSSE} from "../types/jobDataTypes";
export default function MatchPage() {
    const {openSSE, jobsRunning} = useSSEProvider();

    const [groupedClips, setGroupedClips] = useState<GroupedClips | undefined>()
    const groupedClipsRef = useRef<GroupedClips | undefined>()
    const runningJobsRef = useRef<jobSSE[]>([])
    const [loadError, setLoadError] = useState<LoadError>({error: false, message: ''})
    const [selectedEvent, setSelectedEvent] = useState<Event | undefined>();
    const [searchParams] = useSearchParams();
    const intervalRef = useRef<any>(null);
    const eventStreamRef = useRef<string>('');
    const {setContextSelectedEvent} = useSelectedEvent();
    const [eventStream, setEventStream] = useState<string>("");
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const sortAscRef = useRef<boolean>(true);
    const shouldPoll = useRef<boolean>(true);
    const [clipDeleting, setClipDeleting] = useState<boolean>(false);
    const [firstLoading, setFirstLoading] = useState<boolean>(false);
    const [minRunningJobsStatus, setMinRunningJobsStatus] = useState<string>('');
    const minRunningJobsStatusRef = useRef('')

    const {addVideoContentTagsOptions} = useVideoContentTagsOptions()

    const [updatingVideoContent, setUpdatingVideoContent] = useState<boolean>(false);
    const updatingVideoContentRef = useRef<boolean>(false);
    const abortControllerGroupedClips = useRef<AbortController | null>(null);

    useEffect(() => {
        if (updatingVideoContent && abortControllerGroupedClips.current) {
            abortControllerGroupedClips.current.abort();
        }
        updatingVideoContentRef.current = updatingVideoContent
    }, [updatingVideoContent]);

    function synchronizeArrays(array1: any[], array2: any[], idField: string) {
        const idsInSecondArray = new Set(array2.map(item => item[idField]));
        array2.forEach(item => {
            if (!array1.some(existingItem => existingItem[idField] === item[idField])) {
                array1.unshift(item);
            }
        });
        return array1.filter(item => idsInSecondArray.has(item[idField]));
    }

    async function onDeleteClip(clip: Clip, highlight = false) {
        try {
            if (groupedClips) {
                setClipDeleting(true)
                shouldPoll.current = false
                const id = clip.id
                const tempGroupedClips = {...groupedClipsRef.current} as GroupedClips;
                await deleteClip(id);
                const jobGroupIndex = groupedClips.jobGroups.findIndex(jobGroup => jobGroup.id === clip.job_id)
                const jobGroup = tempGroupedClips.jobGroups[jobGroupIndex];
                const jobClipIndex = jobGroup.clips.findIndex(clip => clip.id === id);
                if (jobClipIndex > -1) {
                    if (jobGroup.clips.length > 1) {
                        jobGroup.clips.splice(jobClipIndex, 1);
                    } else {
                        tempGroupedClips.jobGroups.splice(jobGroupIndex, 1);
                    }
                } else {
                    setLoadError({error: true, message: 'Job Clip already deleted'});
                    return
                }
                const keyMomentIndex = tempGroupedClips.keyMomentGroups.findIndex(keyMomentGroup => {
                    const keyMomentType = keyMomentGroup.key_moment.split("_")[1]
                    return keyMomentType === clip.key_moment_type && keyMomentGroup.game_time === clip.game_time
                });
                const keyMomentGroup = tempGroupedClips.keyMomentGroups[keyMomentIndex];
                //Se sto cancellando la main clip
                if (keyMomentGroup.main_clip === id) {
                    //Se ci sono altre clip oltre alla main
                    if (keyMomentGroup.clips.length > 1) {
                        keyMomentGroup.main_clip = keyMomentGroup.clips[0].id;
                        keyMomentGroup.clips.splice(0, 1);
                    } else {
                        tempGroupedClips.keyMomentGroups.splice(keyMomentIndex, 1);
                    }
                } else {
                    const clipIndex = keyMomentGroup.clips.findIndex(clip => clip.id === id);
                    if (clipIndex > -1) {
                        keyMomentGroup.clips.splice(clipIndex, 1);
                    } else {
                        setLoadError({error: true, message: 'Key Moment Clip already deleted'});
                        return
                    }
                }
                const clipIndex = tempGroupedClips.clips.findIndex(clip => clip.id === id);
                if (clipIndex > -1) {
                    groupedClips.clips.splice(clipIndex, 1);
                } else {
                    setLoadError({error: true, message: 'Clips Clip already deleted'});
                    return
                }
                setGroupedClips(tempGroupedClips)
                shouldPoll.current = true
            }
        } catch (e) {
            console.error(e);
            shouldPoll.current = true
            setLoadError({error: true, message: 'Problems deleting the clip'});
        } finally {
            setClipDeleting(false)
        }
    }

    async function deleteAllJobClips(jobId: string) {
        try {
            if (groupedClips) {
                shouldPoll.current = false
                await deleteJobClips(jobId);
                const tempGroupedClips = {...groupedClipsRef.current} as GroupedClips;
                for (let keyMomentIndex = 0; keyMomentIndex < tempGroupedClips.keyMomentGroups.length; keyMomentIndex++) {
                    const keyMomentGroup = tempGroupedClips.keyMomentGroups[keyMomentIndex]
                    for (let clipIndex = 0; clipIndex < keyMomentGroup.clips.length; clipIndex++) {
                        const clip = keyMomentGroup.clips[clipIndex];
                        if (clip.job_id === jobId) {
                            if (clip.id === keyMomentGroup.main_clip && keyMomentGroup.clips[0]) {
                                keyMomentGroup.main_clip = keyMomentGroup.clips[0].id;
                            }
                            keyMomentGroup.clips.splice(clipIndex, 1);
                            clipIndex--;
                        }
                    }
                    if (keyMomentGroup.clips.length === 0) {
                        tempGroupedClips.keyMomentGroups.splice(keyMomentIndex, 1);
                        keyMomentIndex--
                    }
                }
                const jobGroupIndex = tempGroupedClips.jobGroups.findIndex(jobGroup => jobGroup.id === jobId);
                tempGroupedClips.jobGroups.splice(jobGroupIndex, 1);

                for (let clipIndex = 0; clipIndex < tempGroupedClips.clips.length; clipIndex++) {
                    const clip = tempGroupedClips.clips[clipIndex];
                    if (clip.job_id === jobId) {
                        tempGroupedClips.clips.splice(clipIndex, 1);
                        clipIndex--;
                    }
                }
                setGroupedClips(tempGroupedClips);
                shouldPoll.current = true
            }
        } catch (e) {
            console.error(e);
            shouldPoll.current = true
            setLoadError({error: true, message: 'Problems deleting the clips'});
        }
    }

    async function deleteKeyMomentClips(keyMoment: string) {
        try {
            if (groupedClips) {
                shouldPoll.current = false
                const keyMomentType = keyMoment.split("_")[1]
                const gameTime = keyMoment.split("_")[0]
                const tempGroupedClips = {...groupedClipsRef.current} as GroupedClips;
                const promiseArray: any[] = [];
                const keyMomentIndex = tempGroupedClips.keyMomentGroups.findIndex(keyMomentGroup => keyMomentGroup.key_moment.includes(keyMoment));
                const selectedKeyMomentGroup = tempGroupedClips.keyMomentGroups[keyMomentIndex];
                for (const clip of selectedKeyMomentGroup.clips) {
                    promiseArray.push(deleteClip(clip.id))
                }
                await Promise.all(promiseArray)
                for (let clipIndex = 0; clipIndex < tempGroupedClips.clips.length; clipIndex++) {
                    const clip = tempGroupedClips.clips[clipIndex];
                    if (clip.key_moment_type === keyMomentType && clip.game_time === gameTime) {
                        tempGroupedClips.clips.splice(clipIndex, 1);
                        clipIndex--;
                    }
                }

                for (let jobGroupIndex = 0; jobGroupIndex < tempGroupedClips.jobGroups.length; jobGroupIndex++) {
                    const jobGroup = tempGroupedClips.jobGroups[jobGroupIndex];
                    for (let clipIndex = 0; clipIndex < jobGroup.clips.length; clipIndex++) {
                        const clip = jobGroup.clips[clipIndex];
                        if (clip.key_moment_type === keyMomentType && clip.game_time === gameTime) {
                            jobGroup.clips.splice(clipIndex, 1);
                            clipIndex--
                        }
                    }
                    if (jobGroup.clips.length === 0) {
                        tempGroupedClips.jobGroups.splice(jobGroupIndex, 1);
                        jobGroupIndex--
                    }
                }

                tempGroupedClips.keyMomentGroups.splice(keyMomentIndex, 1);

                setGroupedClips(tempGroupedClips);
                shouldPoll.current = true

            }
        } catch (e) {
            shouldPoll.current = true
            console.error(e)
            setLoadError({error: true, message: 'Problems deleting the key moment clips'});
        }
    }

    async function onDeleteHighlight(highlight: Highlight) {
        try {
            if (groupedClips) {
                shouldPoll.current = false
                setClipDeleting(true)
                await deleteHighlight(highlight.id)
                const tempGroupedClips = {...groupedClipsRef.current} as GroupedClips;
                const highlightIndex = tempGroupedClips.highlights.findIndex(hg => hg.id === highlight.id);
                if (highlightIndex > -1) {
                    tempGroupedClips.highlights.splice(highlightIndex, 1);
                    setGroupedClips(tempGroupedClips);
                } else {
                    setLoadError({error: true, message: 'Highlight already removed'});
                }
                shouldPoll.current = true
            }
        } catch (e) {
            setLoadError({error: true, message: 'Problems deleting the highlight'});
            shouldPoll.current = true
        } finally {
            setClipDeleting(false)
        }
    }

    function localUpdateTags(videoContentId: string, tags: string[], contentType: VideoContentTypes, tempGroupedClips: GroupedClips) {
        if (contentType === 'clip') {
            for (const keyMomentGroup of tempGroupedClips.keyMomentGroups) {
                for (const keyMomentClip of keyMomentGroup.clips) {
                    if (keyMomentClip.id === videoContentId) {
                        keyMomentClip.tags = tags;
                    }
                }
            }
            for (const jobGroup of tempGroupedClips.jobGroups) {
                for (const jobClip of jobGroup.clips) {
                    if (jobClip.id === videoContentId) {
                        jobClip.tags = tags;
                    }
                }
            }
            const clip = tempGroupedClips.clips.find(clip => clip.id === videoContentId);
            if (clip) {
                clip.tags = tags;
            }
        }
        if (contentType === 'highlight') {
            const highlight = tempGroupedClips.highlights.find(highlight => highlight.id === videoContentId);
            if (highlight) {
                highlight.tags = tags;
            }
        }
        setGroupedClips(tempGroupedClips)
    }

    async function updateVideoContent(videoContentId: string, contentType: VideoContentTypes, field: VideoUpdatableFields, value: string[] | string) {
        setUpdatingVideoContent(true)
        const tempGroupedClips = {...groupedClipsRef.current} as GroupedClips;
        const originalContent = contentType === 'clip' ? tempGroupedClips.clips.find(clip => clip.id === videoContentId) : tempGroupedClips.highlights.find(highlight => highlight.id === videoContentId);
        let prevTags: string[] = []
        if (originalContent && field === 'tags') {
            prevTags = originalContent.tags ? JSON.parse(JSON.stringify(originalContent.tags)) : []
        }
        try {
            if (field === 'tags') {
                const tags = value as string[]
                localUpdateTags(videoContentId, tags, contentType, tempGroupedClips)
                await updateClipTags(videoContentId, tags, contentType)
                addVideoContentTagsOptions(tags)
            }
        } catch (e) {
            if (field === 'tags') {
                localUpdateTags(videoContentId, prevTags, contentType, tempGroupedClips)
                setLoadError({error: true, message: 'Problems updating the clip tags'});
            }
        } finally {
            setUpdatingVideoContent(false)
        }
    }

    function markClip(editingJobClips: Set<string | undefined>, clip: Clip) {
        let change = false
        const clipPreset = editingJobClips.has(clip.id)
        if (clipPreset && !clip.editing) {
            change = true
            clip.editing = true
        } else if (!clipPreset && clip.editing) {
            change = true
            clip.editing = false
        }
        return change
    }

    function markEditedClips(jobsRunning: jobSSE[], groupClips: GroupedClips) {
        let change = false;
        const editingJob = jobsRunning.filter(job => job.type === "editing" && String(job.settings.aspect_ratio) === '16:9').map(job => job.settings.video_content_id)
        const editingJobClips = new Set(editingJob)
        for (const groupClip of groupClips.keyMomentGroups) {
            for (const clip of groupClip.clips) {
                const localChange = markClip(editingJobClips, clip)
                if (localChange) change = true
            }
        }
        for (const jobGroup of groupClips.jobGroups) {
            for (const jobClip of jobGroup.clips) {
                const localChange = markClip(editingJobClips, jobClip)
                if (localChange) change = true
            }
        }
        for (const clip of groupClips.clips) {
            const localChange = markClip(editingJobClips, clip)
            if (localChange) change = true
        }
        return {groupClips, change}
    }

    useEffect(() => {
        runningJobsRef.current = jobsRunning
        if (groupedClips) {
            const editMarkGroupClips = markEditedClips(runningJobsRef.current, {...(groupedClips as any)})
            if (editMarkGroupClips.change) {
                setGroupedClips(editMarkGroupClips.groupClips)
            }
        }
    }, [jobsRunning]);

    useEffect(() => {
        const eventId = searchParams.get('eventId')
        if (eventId) {
            setFirstLoading(true)
            getEventById((eventId as string)).then(event => {
                setSelectedEvent(event);
                setContextSelectedEvent(event);
            }).finally(() => setFirstLoading(false));
            getMatchClips(eventId).then(groupedClips => {
                groupedClipsSort(groupedClips)
                setGroupedClips(markEditedClips(runningJobsRef.current, groupedClips).groupClips);
                setEventStream(groupedClips.streamUrl);
                setMinRunningJobsStatus(groupedClips.minRunningJobStatus)
                const intervalFunc = async () => {
                    if (!updatingVideoContentRef.current) {
                        try {
                            if (shouldPoll.current) {
                                shouldPoll.current = false
                                abortControllerGroupedClips.current = new AbortController();
                                const newGroupedClips = await getMatchClips(eventId, abortControllerGroupedClips.current);
                                if (!isEqual(newGroupedClips, groupedClips)) {
                                    groupedClipsSort(newGroupedClips);
                                    setGroupedClips(markEditedClips(runningJobsRef.current, newGroupedClips).groupClips);
                                }
                                if (eventStreamRef.current === '' && newGroupedClips.streamUrl !== '') {
                                    setEventStream(newGroupedClips.streamUrl)
                                }
                                if (minRunningJobsStatusRef.current === '' && newGroupedClips.minRunningJobStatus !== '') {
                                    setMinRunningJobsStatus(newGroupedClips.minRunningJobStatus)
                                }
                                shouldPoll.current = true
                            }
                        } catch (error) {
                            if (!axios.isCancel(error)) {
                                console.error("Error to find get data for ", error)
                            }
                            shouldPoll.current = true
                        }
                    }
                }
                if (!intervalRef.current){
                    intervalRef.current = setInterval(intervalFunc, 5000)
                }
            });
        }

        return (() => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        })
    }, []);

    useEffect(() => {
        eventStreamRef.current = eventStream;
    }, [eventStream]);

    useEffect(() => {
        groupedClipsRef.current = groupedClips
    }, [groupedClips]);

    useEffect(() => {
        openSSE()
        /*getMatchAllClips(selectedMatchId)
            .then((data : Match) => {
                console.log(data);
                setMatchData(data);
                setMatchPageDataLoading(false);
                setLoadError({error:false, message: ''})
            })
            .catch((error: any) => (setLoadError({error:true, message: error.message})))*/
    }, []);

    function groupedClipsSort(tempGroupedClips: GroupedClips) {
        for (const jobGroup of tempGroupedClips.jobGroups) {
            jobGroup.clips.sort((clip1, clip2) => sortClipByEventStream(clip1, clip2, sortAscRef.current))
        }
        tempGroupedClips.keyMomentGroups.sort((clip1, clip2) => sortClipByEventStream(clip1, clip2, sortAscRef.current))
    }

    useEffect(() => {
        sortAscRef.current = sortAsc
        if (groupedClips) {
            const tempGroupedClips = {...groupedClips};
            groupedClipsSort(tempGroupedClips);
            setGroupedClips(tempGroupedClips);
        }
    }, [sortAsc])

    return (
        <SelectedClipProvider>
            <PageContainer>
                <MatchPlayerContainer
                    event={selectedEvent}
                    groupedClips={groupedClips}
                    streamUrl={eventStream}
                    onDeleteClip={onDeleteClip}
                    onDeleteAllJobClips={deleteAllJobClips}
                    onDeleteAllKeyMomentClips={deleteKeyMomentClips}
                    onDeleteHighlight={onDeleteHighlight}
                    setSortAsc={setSortAsc}
                    sortAsc={sortAsc}
                    clipDeleting={clipDeleting}
                    firstLoading={firstLoading}
                    onVideoContentUpdate={updateVideoContent}
                    minRunningJobsStatus = {minRunningJobsStatus}
                />
                <ErrorSnackBar
                    autoHideDuration={5000}
                    open={loadError.error}
                    setClose={() => setLoadError({message: '', error: false})}
                    errorMessage={loadError.message}
                />
            </PageContainer>
        </SelectedClipProvider>
    )
}